import * as React from 'react';
import * as DateHelpers from './DateHelpers';
import NotesPopup from './NotesPopup';
import NewJobPricelistPowerPoleQuestionsData from './NewJobPricelistPowerPoleQuestions';
import { ItemOptionList, NewJobQuoteItem, PowerPoleQuestions } from './models/JobInterfaces';
import { GenericList } from './models/JobSharedInterfaces';

interface NewJobPricelistPowerPoleProps {
    jobTypeId: number;
    customerQuoteNotesDunny: string;
    scheduleDateDefaultDays: number;
    allowAllocateToPastDate: boolean;
    jobInstalled: boolean;
    powerPoleQuestions: PowerPoleQuestions;
    gateCode: string;
    requirePickup: boolean;
    quoteItems: NewJobQuoteItem[];
    itemOptions: ItemOptionList[];
    yesNoOptions: GenericList[];
    superSafeSupplierOptions: GenericList[];
    powerPoleLocationOptions: GenericList[];
    powerPoleDistanceOptions: GenericList[];
    customerChargeFuelSurcharge: boolean;
    chargeFuelSurchargeSwitchedOn: boolean;
    errors: { [key: string]: string };
    addItem(quoteItem: NewJobQuoteItem, calculatePrice: boolean): void;
    deleteItem(itemIndex: number): void;
    updateQuoteItem(quoteItem: NewJobQuoteItem, calculatePrice: boolean): void;
    updatePowerPoleQuestions(powerPoleQuestions: PowerPoleQuestions, calculatePrice: boolean): void;
    updateGateCode(gateCode: string): void;
}

interface EditNote {
    heading: string;
    rowIndex: number;
    parentIndex: number;
    id: number;
    itemId: number;
    item: string;
    notes: string;
}

const NewJobPricelistPowerPoleData = (props: NewJobPricelistPowerPoleProps) => {
    const [showEditNotesModal, setShowEditNotesModal] = React.useState(false);
    const [editNote, setEditNote] = React.useState<EditNote>({
        heading: "",
        id: 0,
        item: "",
        itemId: -1,
        notes: "",
        parentIndex: -1,
        rowIndex: -1
    });

    const handleCellChange = (e: any, itemIndex: number) => {
        let quoteItems = props.quoteItems;
        let item = quoteItems[itemIndex];

        let quoteItem: any = {};
        quoteItem = item;
        var value = e.target.value * 1;
        quoteItem[e.target.name] = value;

        if (e.target.name === "itemId") {
            var itemOption = props.itemOptions.find(i => i.id === value);
            var isUnderground = false;
            item.customHirePeriod = false;
            item.hirePeriod = -1;
            if (itemOption) {
                item.hirePeriod = itemOption.priceListDefaultDays;
                item.defaultHirePeriod = itemOption.priceListDefaultDays;
                item.installOnly = itemOption.installOnly;
                item.truck = itemOption.defaultTruckId;
                item.isEws = itemOption.isEws;
                item.isUnderground = itemOption.isUnderground;
                isUnderground = itemOption.isUnderground;
                item.itemTypeId = itemOption.typeId;
                item.item = itemOption.name;
                item.itemDescription = itemOption.description;
                //clear any extras as camera items don't have extras
                item.extras.itemId = -1;
                item.extras.quantity = 0;
                item.extendable = itemOption.extendable;
                item.invoicePeriodTypeId = itemOption.invoicePeriodTypeId;
                item.productGroupId = itemOption.productGroupId;
            }

            //reset underground / above ground questions
            resetUnderAboveGroundQuestions(isUnderground);
        }

        if (itemIndex === (quoteItems.length - 1)) {
            var newItem = addBlankQuoteItem();
            props.addItem(newItem, false);
        }

        if (e.target.name === "amount" || e.target.name === "travel" || e.target.name === "overhireRate") {
            //don't calculate price when updating amount / travel / overhire rate
            props.updateQuoteItem(quoteItem, false);
        } else {
            //calculate price
            props.updateQuoteItem(quoteItem, true);
        }
    }

    const handleCellAmountChange = (e: any, itemIndex: number) => {
        let quoteItems = props.quoteItems;
        let item = quoteItems[itemIndex];
        item.amount = e.target.value;

        //calculate initial hire
        item.initialHire = (e.target.value * 1) - item.travel - item.fuelSurcharge;

        if (itemIndex === (quoteItems.length - 1)) {
            var newItem = addBlankQuoteItem();
            props.addItem(newItem, false);
        }

        //don't calculate price when updating amount
        props.updateQuoteItem(item, false);
    }

    const handleCellPriceChange = (e: any, itemIndex: number) => {
        let quoteItems = props.quoteItems;
        let item = quoteItems[itemIndex];

        let quoteItem: any = {};
        quoteItem = item;
        var value = e.target.value * 1;
        quoteItem[e.target.name] = value;

        //calculate amount
        item.amount = item.initialHire + item.trench + item.cleaning + item.travel + item.fuelSurcharge;

        if (itemIndex === (quoteItems.length - 1)) {
            var newItem = addBlankQuoteItem();
            props.addItem(newItem, false);
        }

        //don't calculate price when updating initial hire / travel
        props.updateQuoteItem(quoteItem, false);
    }

    const handleCellChangeText = (e: any, itemIndex: number) => {
        let quoteItems = props.quoteItems;
        let item = quoteItems[itemIndex];

        let quoteItem: any = {};
        quoteItem = item;
        var value = e.target.value;
        quoteItem[e.target.name] = value;

        if (itemIndex === (quoteItems.length - 1)) {
            var newItem = addBlankQuoteItem();
            props.addItem(newItem, false);
        }

        props.updateQuoteItem(item, false);
    }

    const handleScheduleDateChange = (e: any, itemIndex: number) => {
        let quoteItems = props.quoteItems;
        let item = quoteItems[itemIndex];

        let quoteItem: any = {};
        quoteItem = item;
        var value = e.target.value;
        quoteItem[e.target.name] = value;
        quoteItem["onHireDate"] = value;

        if (itemIndex === (quoteItems.length - 1)) {
            var newItem = addBlankQuoteItem();
            props.addItem(newItem, false);
        }

        props.updateQuoteItem(item, false);
    }

    const handleCellDateChange = (e: any, itemIndex: number) => {
        let quoteItems = props.quoteItems;
        let item = quoteItems[itemIndex];

        let quoteItem: any = {};
        quoteItem = item;
        var value = e.target.value;
        quoteItem[e.target.name] = value;

        if (itemIndex === (quoteItems.length - 1)) {
            var newItem = addBlankQuoteItem();
            props.addItem(newItem, false);
        }

        props.updateQuoteItem(item, false);
    }

    //Enter custom hire period
    const editCustomHirePeriod = (e: any, itemIndex: number) => {
        e.preventDefault();

        let quoteItems = props.quoteItems;
        let item = quoteItems[itemIndex];
        item.customHirePeriod = true;
        item.hirePeriod = item.hirePeriod <= 0 ? 1 : item.hirePeriod / 7;
        props.updateQuoteItem(item, false);
    }
    //END Custom Hire Period

    const resetUnderAboveGroundQuestions = (isUnderground: boolean) => {
        //reset questions related to underground or above ground power poles when changing the item
        var questions = props.powerPoleQuestions;
        questions.isUnderground = isUnderground;
        questions.clearSightLine = -1;
        questions.pitOnSite = -1;
        questions.pitDistanceId = -1;
        
        props.updatePowerPoleQuestions(questions, false);
    }

    const addBlankQuoteItem = () => {
        var item: NewJobQuoteItem = {
            itemIndex: -1,
            itemId: -1,
            item: "",
            itemTypeId: 0,
            productGroupId: 0,
            itemDescription: "",
            installOnly: false,
            extendable: true,
            isEws: false,
            isWaste: false,
            isUnderground: false,
            isEvent: false,
            unitNumber: "",
            scheduleDate: "",
            onHireDate: "",
            pickupDate: "",
            hirePeriod: 42,
            defaultHirePeriod: 42,
            customHirePeriod: false,
            frequencyId: -1,
            locationId: -1,
            fenceBraceId: -1,
            hours: 0,
            size: 0,
            quantity: 1,
            unitOfMeasure: "",
            initialHire: 0,
            travel: 0,
            cleaning: 0,
            trench: 0,
            confinedSpace: 0,
            fuelSurcharge: 0,
            amount: 0,
            overhireRate: "0.00",
            truck: 0,
            truckDoors: -1,
            purchaseOrderNumber: "",
            notes: "",
            trolley: false,
            extras: {
                itemId: -1,
                quantity: 0,
                equipped: false
            },
            fencingStock: {
                panels: 0,
                blocks: 0,
                clamps: 0,
                pickets: 0,
                signs: 0,
                braces: 0
            },
            calculatedPriceNote: "",
            deliveryAddress: "",
            deliveryAddressGooglePlaceId: "",
            deliveryAddressLatitude: 0,
            deliveryAddressLongitude: 0,
            invoiceDescription: "",
            incStepsTravel: false,
            invoicePeriodTypeId: 1
        };

        return item;
    }

    //NOTES
    const editNotes = (e: any, itemIndex: number) => {
        e.preventDefault();

        let quoteItems = props.quoteItems;
        let item = quoteItems[itemIndex];

        var note: EditNote = {
            heading: "Item Notes",
            parentIndex: 0,
            rowIndex: itemIndex,
            id: 0,
            itemId: item.itemId,
            item: "",
            notes: item.notes
        };

        setEditNote(note);
        setShowEditNotesModal(true);
    }

    const hideNotes = () => {
        let note = resetNotes();
        setEditNote(note);
        setShowEditNotesModal(false);
    }

    const updateNotes = (note: EditNote) => {
        setEditNote(prevState => ({ ...prevState, note }));
        //setEditNote(note);
    }

    const resetNotes = () => {
        //reset
        var note: EditNote = {
            heading: "",
            parentIndex: 0,
            rowIndex: 0,
            id: 0,
            itemId: 0,
            item: "",
            notes: ""
        };

        return note;
    }

    const saveNotes = () => {
        let quoteItems = props.quoteItems;
        let item = quoteItems[editNote.rowIndex];

        item.notes = editNote.notes;

        let note = resetNotes();

        setEditNote(note);
        setShowEditNotesModal(false);
        props.updateQuoteItem(item, false);
    }
    //END NOTES

    const deleteItem = (e: any, itemIndex: number) => {
        e.preventDefault();
        props.deleteItem(itemIndex);
    }

    const notesPopup = <NotesPopup showEditNotesModal={showEditNotesModal} editNotes={editNote} hideNotes={hideNotes} updateNotes={updateNotes} saveNotes={saveNotes} />
    const lastRowIndex = props.quoteItems.length - 1;
    const minDate = props.jobInstalled ? DateHelpers.addDaysToCurrentDate(-1500) : props.allowAllocateToPastDate ? DateHelpers.addDaysToCurrentDate(-90) : DateHelpers.addDaysToCurrentDate(-2);
    const isUnderground = props.quoteItems.length === 0 ? false : props.quoteItems[0].isUnderground;
    const item = props.quoteItems.length > 0 ? props.quoteItems[0] : addBlankQuoteItem();
    const ppQuestions = <NewJobPricelistPowerPoleQuestionsData isReadOnly={false} isUnderground={isUnderground} powerPoleQuestions={props.powerPoleQuestions} gateCode={props.gateCode} yesNoOptions={props.yesNoOptions} powerPoleLocationOptions={props.powerPoleLocationOptions} powerPoleDistanceOptions={props.powerPoleDistanceOptions} superSafeSupplierOptions={props.superSafeSupplierOptions} errors={props.errors} updateGateCode={props.updateGateCode} updatePowerPoleQuestions={props.updatePowerPoleQuestions} />


    return (
        <div>
            <div className="static-modal">
                {notesPopup}
            </div>
            <div>
                <div className="newJobPricelist--customer-notes">{props.customerQuoteNotesDunny}</div>
                <div className="overflowAuto">
                    <table className="table tableColours">
                        <thead>
                            <tr>
                                <th></th>
                                <th>Item</th>
                                <th className={props.jobTypeId === 3 ? "hidden" : ""}>Unit #</th>
                                <th className={props.jobTypeId === 3 ? "hidden" : ""}>Delivery Date</th>
                                <th className={props.jobTypeId === 3 || !props.requirePickup ? "hidden" : ""}>Pickup Date</th>
                                <th>Hire Period</th>
                                <th></th>
                                <th>Quantity</th>
                                <th className={props.jobTypeId === 3 ? "hidden" : ""}>PO #</th>
                                <th>Initial Hire</th>
                                <th>Trench Fee</th>
                                <th>Connection</th>
                                <th>Travel</th>
                                <th>Fuel Surcharge</th>
                                <th>Total Amount</th>
                                <th>Add. Hire Rate</th>
                                <th>Notes</th>
                                <th></th>
                            </tr>
                        </thead>
                        
                        <tbody key={0}>
                            <tr key={0}>
                                <td className="table__text--align">{1}</td>
                                <td className="table__text--align">
                                    <select className="select table__select--size newJobPricelist-item--size" id="itemId" name="itemId" value={item.itemId} onChange={(e) => handleCellChange(e, 0)}>
                                        <option hidden defaultValue="-1"></option>
                                        {props.itemOptions.map(item =>
                                            <option key={item.id} value={item.id}>{item.name}</option>
                                        )};
                                    </select>
                                </td>
                                <td className={props.jobTypeId === 3 ? "hidden" : "table__text--align"}>
                                    <input type='text' maxLength={100} className="input allocate__qtySize" id="unitNumber" name="unitNumber" value={item.unitNumber} onChange={(e) => handleCellChangeText(e, 0)} />
                                </td>
                                <td className={props.jobTypeId === 3 ? "hidden" : "table__text--align"}>
                                    <input type='date' min={minDate} className="input allocate__dateSize" id="scheduleDate" name="scheduleDate" value={item.scheduleDate} onChange={(e) => handleScheduleDateChange(e, 0)} />
                                </td>
                                <td className={props.jobTypeId === 3 || !props.requirePickup ? "hidden" : "table__text--align"}>
                                    <input type='date' min={minDate} className={item.installOnly ? "hidden" : "input allocate__dateSize"} id="pickupDate" name="pickupDate" value={item.pickupDate} onChange={(e) => handleCellDateChange(e, 0)} />
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="1" className={item.customHirePeriod ? "input allocate__size100 textalignright" : "hidden"} id="hirePeriod" name="hirePeriod" value={item.hirePeriod} onChange={(e) => handleCellChange(e, 0)} />
                                    <select className={item.customHirePeriod ? "hidden" : "select table__select--size"} id="hirePeriod" name="hirePeriod" value={item.hirePeriod} onChange={(e) => handleCellChange(e, 0)} disabled={item.itemTypeId === 57 } >
                                        <option hidden defaultValue="-1"></option>
                                        <option hidden={item.itemTypeId != 57} value={1}>1 Day</option>
                                        <option value={182}>26 Weeks</option>
                                        <option value={252}>36 Weeks</option>
                                        <option value={364}>52 Weeks</option>
                                    </select>
                                </td>
                                <td className="table__text--align">
                                    <div className={item.customHirePeriod || item.itemId < 1 || item.itemTypeId === 57 ? "hidden" : "tooltipitem tooltipitem__right"}><i className="fa-solid fa-pen-to-square" onClick={(e) => editCustomHirePeriod(e, 0)}></i>
                                        <span className="tooltipitemtext tooltipitemtext__right">Enter Hire Period in Weeks</span>
                                    </div>
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="1" className='input allocate__qtySize textalignright' id="quantity" name="quantity" value={item.quantity} onChange={(e) => handleCellChange(e, 0)} disabled />
                                </td>
                                <td className={props.jobTypeId === 3 ? "hidden" : "table__text--align"}>
                                    <input type='text' maxLength={25} className="input table-input--minwidth" id="purchaseOrderNumber" name="purchaseOrderNumber" value={item.purchaseOrderNumber} onChange={(e) => handleCellChangeText(e, 0)} />
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="0.01" className='input textalignright newJobPricelist-amount--sizesm' id="initialHire" name="initialHire" value={item.initialHire} onChange={(e) => handleCellPriceChange(e, 0)} />
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="0.01" className='input textalignright newJobPricelist-amount--sizesm' id="trench" name="trench" value={item.trench} onChange={(e) => handleCellPriceChange(e, 0)} />
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="0.01" className='input textalignright newJobPricelist-amount--sizesm' id="cleaning" name="cleaning" value={item.cleaning} onChange={(e) => handleCellPriceChange(e, 0)} />
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="0.01" className='input textalignright newJobPricelist-amount--sizesm' id="travel" name="travel" value={item.travel} onChange={(e) => handleCellPriceChange(e, 0)} />
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="0.01" className='input textalignright newJobPricelist-amount--sizesm' id="fuelSurcharge" name="fuelSurcharge" value={item.fuelSurcharge} onChange={(e) => handleCellPriceChange(e, 0)} />
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="0.01" className='input textalignright newJobPricelist-amount--size' id="amount" name="amount" value={item.amount} onChange={(e) => handleCellAmountChange(e, 0)} />
                                </td>
                                <td className="table__text--align">
                                    <input type='number' min="0" step="0.01" className='input textalignright newJobPricelist-amount--sizesm' id="overhireRate" name="overhireRate" value={item.overhireRate} onChange={(e) => handleCellChange(e, 0)} />
                                </td>
                                <td className="table__text--align">
                                    <a className={!item.notes ? "hidden" : ""} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, 0)}>
                                        <div className="tooltipitem tooltipitem__left">{item.notes ? item.notes.substring(0, 20) + "... " : ""}
                                            <span className="tooltipitemtext tooltipitemtext__left">{item.notes}</span>
                                        </div>
                                    </a>
                                    <a className={!item.notes && lastRowIndex != 0 ? "" : "hidden"} data-toggle="modal" data-target="#notesModal" href="#" onClick={(e) => editNotes(e, 0)}>
                                        <span className="fas fa-pencil-alt edit--icon alignIconCenter"></span>
                                    </a>
                                </td>
                                <td className="table__text--align">
                                    {item.itemId > 0 ?
                                        <div className="delete--tablecell">
                                            <a className="makeitred" href="#" onClick={(e) => deleteItem(e, item.itemIndex)}>
                                                <span className="fas fa-times-circle edit--icon alignIconCenter"></span>
                                            </a>
                                        </div> : ""
                                    }
                                </td>
                            </tr>


                        </tbody>
                    </table>
                    {props.errors["powerPoleQuoteItems"] ?
                        (props.errors["powerPoleQuoteItems"]).split("\n").map((item: any, key: any) => {
                            return <span className="label errors" key={key}>{item}<br /></span>
                        })
                        : ""}
                </div>
            </div>
            <div>
                {ppQuestions}
            </div>
        </div>
    )

}

export default NewJobPricelistPowerPoleData;